// src/components/common/AlertDialog.tsx
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCircleCheck, faCircleExclamation, faCircleInfo, faTriangleExclamation } from '@/icons';
import Modal from './Modal';
import './AlertDialog.css';

export type AlertDialogVariant = 'success' | 'error' | 'info' | 'warning';

interface AlertDialogProps {
  isOpen: boolean;
  theme: 'dark' | 'light';
  variant?: AlertDialogVariant;
  title?: string;
  message: string;
  mode: 'alert' | 'confirm';
  onConfirm: () => void;
  onCancel?: () => void;
}

const VARIANT_ICONS: Record<AlertDialogVariant, IconDefinition> = {
  success: faCircleCheck,
  error: faCircleExclamation,
  info: faCircleInfo,
  warning: faTriangleExclamation,
};

const VARIANT_TITLES: Record<AlertDialogVariant, string> = {
  success: 'Sucesso',
  error: 'Erro',
  info: 'Informação',
  warning: 'Aviso',
};

const AlertDialog: React.FC<AlertDialogProps> = ({
  isOpen,
  theme,
  variant = 'info',
  title,
  message,
  mode,
  onConfirm,
  onCancel,
}) => {
  const resolvedTitle = title ?? VARIANT_TITLES[variant];

  return (
    <Modal isOpen={isOpen} onClose={onConfirm} theme={theme} closable={false}>
      <div className="alert-dialog" data-variant={variant}>
        <div className="alert-dialog-header">
          <FontAwesomeIcon icon={VARIANT_ICONS[variant]} className="alert-dialog-icon" />
          <h3 className="alert-dialog-title">{resolvedTitle}</h3>
        </div>
        <p className="alert-dialog-message">{message}</p>
        <div className="alert-dialog-buttons">
          {mode === 'confirm' && onCancel && (
            <button
              type="button"
              className="alert-dialog-button alert-dialog-button-cancel"
              onClick={onCancel}
            >
              Cancelar
            </button>
          )}
          <button
            type="button"
            className="alert-dialog-button alert-dialog-button-confirm"
            onClick={onConfirm}
            autoFocus
          >
            OK
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AlertDialog;
